import { VuexModule, Module, Mutation, Action, getModule } from 'vuex-module-decorators';
import ajax from '@/lib/ajax';
import store from '@/store';
import { AppModule } from '@/store/Auth/AppModule';
import { AuthModule } from '@/store/Auth/AuthModule';
import LendingAccount from '@/models/Accounts/LendingAccounts/LendingAccount';
import FilterAccountsRequest from '@/models/Shared/FilterAccountsRequest';
import UpdateAccountRequest from '@/models/Accounts/Shared/UpdateAccountRequest';
import UpdateAccountStateRequest from '@/models/Accounts/Shared/UpdateAccountStateRequest';
import UpdateAccountGeneralProductSettingsRequest from '@/models/Accounts/Shared/UpdateAccountGeneralProductSettingsRequest';
import UpdateAccountLendingInterestSettingsRequest from '@/models/Accounts/LendingAccounts/Ledgers/Settings/LendingInterest/UpdateAccountLendingInterestSettingsRequest';
import AccountSearchType from '@/models/Shared/AccountSearchType';
import Transaction from '@/models/Accounts/Shared/Transactions/Transaction';
import TransactionsOrderOptions from "@/models/Accounts/Shared/Transactions/TransactionsOrderOptions";
import Card from '@/models/Accounts/Shared/Cards/Card';
import CreateCardRequest from '@/models/Accounts/Shared/Cards/CreateCardRequest';
import UpdateCardRequest from '@/models/Accounts/Shared/Cards/UpdateCardRequest';
import CreateRepaymentPlanRequest from '@/models/Accounts/LendingAccounts/RepaymentPlans/CreateRepaymentPlanRequest';
import ProductFeatureVisiblity from '@/models/Products/ProductFeatureVisiblity';
import Product from '@/models/Products/Product';
import ProductType from "@/models/Products/ProductType";
import ProductIssue from '@/models/Products/ProductIssues/ProductIssue';
import Schedule from '@/models/Accounts/LendingAccounts/Schedules/Schedule';
import Simulation from '@/models/Accounts/Shared/Toolkit/Simulation';
import CreateSimulationRequest from '@/models/Accounts/Shared/Toolkit/CreateSimulationRequest';
import UpdateSimulationRequest from '@/models/Accounts/Shared/Toolkit/UpdateSimulationRequest';
import HistoryEntry from '@/models/Shared/HistoryEntry';
import GetHistoryResponse from '@/models/Shared/GetHistoryResponse';
import PermissionUtils from '@/lib/permissionUtils';
import ListUtils from '@/lib/listUtils';
import ValidationUtils from '@/lib/validationUtils';

export interface ILendingAccountState {
    loadingLendingAccounts: boolean;
    loadingLendingAccount: boolean;
    loadingCards: boolean;
    loadingSchedule: boolean;
    loadingRepaymentPlans: boolean;
    loadingDialog: boolean;
    loadingHistory: boolean;
    loadingTransactions: boolean;
    loadingPendingTransactions: boolean;
    lendingAccount: LendingAccount | null;
    totalLendingAccountsPages: number;
    lendingAccounts: LendingAccount[];
    lendingAccountsSearch: string;
    lendingAccountsSearchType: AccountSearchType;
    currentLendingAccountTransactions: Transaction[];
    totalLendingAccountTransactionsPages: number;
    currentLendingAccountPendingTransactions: Transaction[];
    totalLendingAccountPendingTransactionsPages: number;
    currentLendingAccountTransactionsOrder: TransactionsOrderOptions;
    currentLendingAccountTransactionsSearch: string;
    cards: Card[];
    totalCardsPages: number;
    currentCard: Card;
    schedule: Schedule;
    totalSchedulePages: number;
    repaymentPlans: [];
    totalRepaymentPlansPages: number;
    currentRepaymentPlan: {};
    productFeatureVisibility: ProductFeatureVisiblity;
    productOptions: Product[];
    productIssueOptions: ProductIssue[];
    simulationModeEnabled: boolean;
    simulations: Simulation[];
    simulation: Simulation;
    history: HistoryEntry[];
    pageSize: number;
}

@Module({ dynamic: true, store, name: 'LendingAccountModule', namespaced: true })
class LendingAccountStore extends VuexModule implements ILendingAccountState {

    public loadingLendingAccounts: boolean = true;
    public loadingLendingAccount: boolean = true;
    public loadingCards: boolean = true;
    public loadingSchedule: boolean = true;
    public loadingRepaymentPlans: boolean = true;
    public loadingDialog: boolean = false;
    public loadingHistory: boolean = true;
    public loadingTransactions: boolean = false;
    public loadingPendingTransactions: boolean = true;
    public lendingAccount: LendingAccount = new LendingAccount();
    public totalLendingAccountsPages: number = 1;
    public lendingAccounts: LendingAccount[] = [];
    public lendingAccountsSearch: string = '';
    public lendingAccountsSearchType: AccountSearchType = AccountSearchType.AccountName;
    public currentLendingAccountTransactions: Transaction[] = [];
    public totalLendingAccountTransactionsPages: number = 1;
    public currentLendingAccountPendingTransactions: Transaction[] = [];
    public totalLendingAccountPendingTransactionsPages: number = 1;
    public currentLendingAccountTransactionsOrder: TransactionsOrderOptions = TransactionsOrderOptions.DisplayDate;
    public currentLendingAccountTransactionsSearch: string = '';
    public cards: Card[] = [];
    public totalCardsPages: number = 1;
    public currentCard: Card = new Card();
    public schedule: Schedule = new Schedule();
    public totalSchedulePages: number = 1;
    public repaymentPlans: [] = [];
    public totalRepaymentPlansPages: number = 1;
    public currentRepaymentPlan: {} = {};
    public productFeatureVisibility = new ProductFeatureVisiblity();
    public productOptions: Product[] = [];
    public productIssueOptions: ProductIssue[] = [];
    public simulationModeEnabled: boolean = false;
    public simulations: Simulation[] = [];
    public simulation: Simulation = new Simulation();
    public history: HistoryEntry[] = [];
    public pageSize: number = PermissionUtils.getPageSize();

    @Action
    async getLendingAccounts(request: FilterAccountsRequest) {

        this.setLoadingLendingAccounts(true);

        // If there is a search value, apply the search parameters here depending on the search type
        let searchParameter = '';
        let encodedLendingAccountsSearch = '';
        if (this.lendingAccountsSearch) {
            encodedLendingAccountsSearch = ValidationUtils.encodeRFC5987ValueChars(this.lendingAccountsSearch);
            if (this.lendingAccountsSearchType === AccountSearchType.AccountName) {
                searchParameter = `&name=${encodedLendingAccountsSearch}`;
            }
            if (this.lendingAccountsSearchType === AccountSearchType.AccountIdentifier) {
                searchParameter = `&paymentAccountIdentifier=${encodedLendingAccountsSearch}`;
            }
            else {
                searchParameter = `&customerName=${encodedLendingAccountsSearch}`;
            }
        }

        // If UX API Access is allowed, use UX API
        if (AuthModule.me.institution.config.uxApiAccess && PermissionUtils.allowUxApiAccess()) {
            await ajax.get(`/portal-backend/ux-api/accounts?productType=${ProductType.Lending}&pageSize=${this.pageSize}&pageNumber=${request.pageNumber}${searchParameter}`)
            .then(response => {
                if (response.data.accounts) {
                    this.context.commit("setLendingAccounts", response.data.accounts);
                }
                else {
                    this.context.commit("resetLendingAccounts");
                }
                this.context.commit("setLendingAccountsTotalPages", response.data.records.totalRecords);
                this.setLoadingLendingAccounts(false);
            })
            .catch(error => {
                AppModule.handleApiError(error);
                this.setLoadingLendingAccounts(false);
            });
        }
        // Else get Accounts from FI instead
        else {
            await ajax.get(`/portal-backend/fi-api/accounts?productType=${ProductType.Lending}&pageSize=${this.pageSize}&pageNumber=${request.pageNumber}${searchParameter}`)
            .then(response => {
                if (response.data.results) {
                    this.context.commit("setLendingAccounts", response.data.results);
                }
                else {
                    this.context.commit("resetLendingAccounts");
                }
                this.context.commit("setLendingAccountsTotalPages", response.data.records.totalRecords);
                this.setLoadingLendingAccounts(false);
            })
            .catch(error => {
                AppModule.handleApiError(error);
                this.setLoadingLendingAccounts(false);
            });
        }
    }

    @Action
    async updateLendingAccountsSearch(searchText: string) {
        // For a smoother UI experience we immidiately set the loading flag
        // and then reset the lending accounts and page count all before
        // searching, this makes the UI look faster and avoids old data hanging around
        this.setLoadingLendingAccounts(true);
        this.context.commit("resetLendingAccounts");
        this.context.commit("setLendingAccountsTotalPages", 1);
        // Then set the search text
        this.context.commit("setLendingAccountsSearch", searchText);
    }

    @Action
    async updateLendingAccountsSearchType(searchType: AccountSearchType) {
        this.context.commit("setLendingAccountsSearchType", searchType);
    }

    @Action
    async getLendingAccount(id: string) {

        // Reset lending account to blank to avoid showing previously loaded account data
        this.context.commit("setLendingAccount", new LendingAccount());

        this.setLoadingLendingAccount(true);

        // If UX API Access is allowed, use UX API
        if (AuthModule.me.institution.config.uxApiAccess && PermissionUtils.allowUxApiAccess()) {
            await ajax.get(`/portal-backend/ux-api/accounts/${id}`)
            .then(response => {
                this.context.commit("setLendingAccount", response.data);
                this.setLoadingLendingAccount(false);
            })
            .catch(error => {
                AppModule.handleApiError(error);
                this.setLoadingLendingAccount(false);
            });
        }
        // Else get Customer Accounts from FI instead
        else {
            await ajax.get(`/portal-backend/fi-api/accounts/${id}`)
            .then(response => {
                this.context.commit("setLendingAccount", response.data);
                this.setLoadingLendingAccount(false);
            })
            .catch(error => {
                AppModule.handleApiError(error);
                this.setLoadingLendingAccount(false);
            });
        }
    }

    @Action
    async editLendingAccount(request: UpdateAccountRequest) {

        this.setLoadingDialog(true);

        // If UX API Access is allowed, use UX API
        if (AuthModule.me.institution.config.uxApiAccess && PermissionUtils.allowUxApiAccess()) {
            await ajax.patch(`/portal-backend/ux-api/accounts/${this.lendingAccount.id}`, request)
            .then(response => {
                this.context.commit("setLendingAccount", response.data);
                this.setLoadingDialog(false);
            })
            .catch(error => {
                AppModule.handleApiError(error);
                this.setLoadingDialog(false);
            });
        }
        // Else get Customer Accounts from FI instead
        else {
            await ajax.patch(`/portal-backend/fi-api/accounts/${this.lendingAccount.id}`, request)
            .then(response => {
                this.context.commit("setLendingAccount", response.data);
                this.setLoadingDialog(false);
            })
            .catch(error => {
                AppModule.handleApiError(error);
                this.setLoadingDialog(false);
            });
        }
    }

    @Action
    async updateLendingAccountState(request: UpdateAccountStateRequest) {

        this.setLoadingDialog(true);

        // If UX API Access is allowed, use UX API
        if (AuthModule.me.institution.config.uxApiAccess && PermissionUtils.allowUxApiAccess()) {
            await ajax.patch(`/portal-backend/ux-api/accounts/${this.lendingAccount.id}/state`, request)
            .then(response => {
                this.context.commit("setLendingAccount", response.data);
                this.setLoadingDialog(false);
            })
            .catch(error => {
                AppModule.handleApiError(error);
                this.setLoadingDialog(false);
            });
        }
        // Else get Customer Accounts from FI instead
        else {
            await ajax.patch(`/portal-backend/fi-api/accounts/${this.lendingAccount.id}/state`, request)
            .then(response => {
                this.context.commit("setLendingAccount", response.data);
                this.setLoadingDialog(false);
            })
            .catch(error => {
                AppModule.handleApiError(error);
                this.setLoadingDialog(false);
            });
        }
    }

    @Action
    async editGeneralProductSettings(request: UpdateAccountGeneralProductSettingsRequest) {

        this.setLoadingDialog(true);

        await ajax.put(`/portal-backend/fi-api/internal/accounts/${this.lendingAccount.id}/general-product-settings`, request)
        .then(response => {
            this.context.commit("setLendingAccount", response.data);
            this.setLoadingDialog(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingDialog(false);
        });
    }

    @Action
    async editLendingInterestSettings(request: UpdateAccountLendingInterestSettingsRequest) {

        this.setLoadingDialog(true);

        // If in simulation mode, create a special request and pass the settings to a different endpoint
        if (this.simulationModeEnabled) {

            const simulationRequest = new UpdateSimulationRequest();

            // Apply the simulation fields to the request
            simulationRequest.name = this.simulation.name;
            simulationRequest.accountId = this.lendingAccount.id;
            simulationRequest.lendingSettings.lendingInterestSettings.rateType = request.rateType;
            simulationRequest.lendingSettings.lendingInterestSettings.rateValue = request.rateValue;
            simulationRequest.lendingSettings.lendingInterestSettings.rateSourceId = request.rateSourceId;

            await ajax.patch(`/portal-backend/fi-api/internal/accounts/${this.lendingAccount.id}/simulation/${this.simulation.id}`, simulationRequest)
            .then(response => {
                this.context.commit("setCurrentSimulation", response.data);
                this.setLoadingDialog(false);
            })
            .catch(error => {
                AppModule.handleApiError(error);
                this.setLoadingDialog(false);
            });
        }
        else {
            await ajax.put(`/portal-backend/fi-api/internal/accounts/${this.lendingAccount.id}/lending-interest-settings`, request)
            .then(response => {
                this.context.commit("setLendingAccount", response.data);
                // After updating the settings, get the new version of the schedule
                this.getSchedule();
                this.setLoadingDialog(false);
            })
            .catch(error => {
                AppModule.handleApiError(error);
                this.setLoadingDialog(false);
            });
        }
    }

    @Action
    async deleteLendingInterestSettings() {

        this.setLoadingDialog(true);

        await ajax.delete(`/portal-backend/fi-api/internal/accounts/${this.lendingAccount.id}/lending-interest-settings`)
        .then(response => {
            this.context.commit("setLendingAccount", response.data);
            this.setLoadingDialog(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingDialog(false);
        });
    }

    @Action
    async getProductOptionsForLendingAccount() {
        await ajax.get(`/portal-backend/fi-api/internal/products?type=${this.lendingAccount.productType}&subType=${this.lendingAccount.productSubType}`)
        .then(response => {
            this.context.commit("setProductOptions", response.data.results);
        })
        .catch(error => {
            AppModule.handleApiError(error);
        });
    }

    @Action
    async getProductIssueOptionsForLendingAccount(productID: string) {
        await ajax.get(`/portal-backend/fi-api/internal/product-issues?productId=${productID}`)
        .then(response => {
            this.context.commit("setProductIssueOptions", response.data.results);
        })
        .catch(error => {
            AppModule.handleApiError(error);
        });
    }

    @Action
    async getLedgerTransactions(data: {id: string, page: number}) {

        this.setLoadingTransactions(true);

        // TODO: Get the real transactions
        this.context.commit("setLendingAccountTransactions", []);
        this.context.commit("setLendingAccountTransactionsTotalPages", 0);

        this.setLoadingTransactions(false);
    }

    @Action
    async getLedgerPendingTransactions(data: {id: string, page: number}) {

        this.setLoadingPendingTransactions(true);

        // TODO: Get the real pending transactions
        this.context.commit("setLendingAccountPendingTransactions", []);
        this.context.commit("setLendingAccountPendingTransactionsTotalPages", 0);

        this.setLoadingPendingTransactions(false);
    }

    @Action
    async updateCurrentLendingAccountTransactionsOrder(order: TransactionsOrderOptions) {
        this.context.commit("setCurrentLendingAccountTransactionsOrder", order);
    }

    @Action
    async updateCurrentLendingAccountTransactionsSearch(searchText: string) {
        this.context.commit("setCurrentLendingAccountTransactionsSearch", searchText);
    }

    @Action
    async createCard(request: CreateCardRequest) {

        this.setLoadingDialog(true);
        this.setLoadingDialog(false);
    }

    @Action
    async getCards(pageNumber: number) {

        const testCards = [
            {
                accountId: "018987ed-b07d-48f4-8345-313ecf8d0f5e",
                cardAccountIdentifier: "cf2cae2f-f897-41f9-a73f-918d8fc45498",
                cardProcessor: "GPS",
                createdAt: "2023-05-12T16:13:42.000Z",
                enabled: true,
                id: "70141db7-cc2e-4407-93ae-caeba64cf5d5",
                personId: "019b921a-f23b-4f30-870d-697a897b9105",
                personIdentifier: {
                    entityId: "019b921a-f23b-4f30-870d-697a897b9105",
                    entityName: "Jimmy Cremin",
                    entityType: "PERSON"
                },
                updatedAt: "2023-05-12T16:13:42.000Z",
                walletId: "798f31c0-4d49-4a54-8e45-4232ac344544"
            }
        ];

        this.setLoadingCards(true);

        // TODO: Get the real cards
        this.context.commit("setCards", testCards);
        this.context.commit("setCardsTotalPages", testCards.length);
        if (testCards.length > 0) {
            this.context.commit("setCurrentCard", testCards[0]);
        }

        this.setLoadingCards(false);
    }

    @Action
    async editCard(request: UpdateCardRequest) {

        this.setLoadingDialog(true);

        //this.context.commit("setCurrentCard", response.data);

        this.setLoadingDialog(false);
    }

    @Action
    public async updateCurrentCard(card: Card){
        this.context.commit("setCurrentCard", card);
    }

    @Action
    async deleteCard(id: string) {

        this.setLoadingCards(true);
        this.setLoadingCards(false);
    }

    @Action
    async getSchedule() {

        this.setLoadingSchedule(true);

        await ajax.get(`/portal-backend/fi-api/internal/accounts/${this.lendingAccount.id}/schedule`)
        .then(response => {
            this.context.commit("setSchedule", response.data);
            this.context.commit("setScheduleTotalPages", response.data.schedule.length);
            this.setLoadingSchedule(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingSchedule(false);
        });
    }

    @Action
    async getRepaymentPlans(page: number) {

        this.setLoadingRepaymentPlans(true);

        // TODO: Get the real repayment plans
        this.context.commit("setRepaymentPlans", []);
        this.context.commit("setRepaymentPlansTotalPages", 0);

        // if (testRepaymentPlans.length > 0) {
        //     this.context.commit("setCurrentRepaymentPlan", testRepaymentPlans[0]);
        // }

        this.setLoadingRepaymentPlans(false);
    }

    @Action
    async updateCurrentRepaymentPlan(repaymentPlan: {}) {
        this.context.commit("setCurrentRepaymentPlan", repaymentPlan);
    }

    @Action
    async createRepaymentPlan(request: CreateRepaymentPlanRequest) {

        this.setLoadingRepaymentPlans(true);

        const newRepaymentPlansList = [ request ];

        this.context.commit("setRepaymentPlans", newRepaymentPlansList);
        this.context.commit("setRepaymentPlansTotalPages", newRepaymentPlansList.length);

        if (newRepaymentPlansList.length > 0) {
            this.context.commit("setCurrentRepaymentPlan", newRepaymentPlansList[0]);
        }

        this.setLoadingRepaymentPlans(false);
    }

    @Action
    async startSimulationMode() {
        this.context.commit("setSimulationMode");
    }

    @Action
    async setSimulation(simulation: Simulation) {
        await ajax.get(`/portal-backend/fi-api/internal/accounts/${this.lendingAccount.id}/simulation/${simulation.id}`)
        .then(response => {
            this.context.commit("setCurrentSimulation", response.data);
            this.setLoadingDialog(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingDialog(false);
        });
    }

    @Action
    async createNewSimulation(simulation: Simulation) {

        // Create request and apply simulation fields
        const simulationRequest = new CreateSimulationRequest();
        simulationRequest.name = simulation.name;

        await ajax.post(`/portal-backend/fi-api/internal/accounts/${this.lendingAccount.id}/simulation`, simulationRequest)
        .then(response => {
            this.context.commit("setCurrentSimulation", response.data);
            this.setLoadingDialog(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingDialog(false);
        });
    }

    @Action
    async getSimulations() {
        await ajax.get(`/portal-backend/fi-api/internal/accounts/${this.lendingAccount.id}/simulation`)
        .then(response => {
            this.context.commit("setSimulations", response.data.results);
            this.setLoadingDialog(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingDialog(false);
        });
    }

    @Action
    async cancelSimulationMode() {
        this.context.commit("clearSimulationMode");
    }

    @Action
    async getHistory() {

        this.setLoadingHistory(true);

        await ajax.get(`/portal-backend/fi-api/internal/accounts/${this.lendingAccount.id}/history`)
        .then(response => {
            this.context.commit("setHistory", response.data);
            this.setLoadingHistory(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingHistory(false);
        });
    }

    @Action
    public async cleanState() {
        this.context.commit("resetState");
    }

    @Mutation
    resetState() {
        this.loadingLendingAccounts = true;
        this.loadingLendingAccount = true;
        this.loadingCards = true;
        this.loadingSchedule = true;
        this.loadingRepaymentPlans = true;
        this.loadingDialog = false;
        this.loadingHistory = true;
        this.loadingTransactions = false;
        this.loadingPendingTransactions = true;
        this.lendingAccount = new LendingAccount();
        this.totalLendingAccountsPages = 1;
        this.currentLendingAccountTransactions = [];
        this.totalLendingAccountTransactionsPages = 1;
        this.currentLendingAccountPendingTransactions = [];
        this.totalLendingAccountPendingTransactionsPages = 1;
        this.currentLendingAccountTransactionsOrder = TransactionsOrderOptions.DisplayDate;
        this.currentLendingAccountTransactionsSearch = '';
        this.lendingAccounts = [];
        this.cards = [];
        this.totalCardsPages = 1;
        this.currentCard = new Card();
        this.schedule = new Schedule();
        this.totalSchedulePages = 1;
        this.repaymentPlans = [];
        this.totalRepaymentPlansPages = 1;
        this.currentRepaymentPlan = {};
        this.productFeatureVisibility = new ProductFeatureVisiblity();
        this.simulationModeEnabled = false;
        this.simulations = [];
        this.simulation = null;
    }

    @Mutation
    private async setLoadingLendingAccounts(loading: boolean) {
        this.loadingLendingAccounts = loading;
    }

    @Mutation
    private async setLoadingLendingAccount(loading: boolean) {
        this.loadingLendingAccount = loading;
    }

    @Mutation
    private async setLoadingCards(loading: boolean) {
        this.loadingCards = loading;
    }

    @Mutation
    private async setLoadingSchedule(loading: boolean) {
        this.loadingSchedule = loading;
    }

    @Mutation
    private async setLoadingRepaymentPlans(loading: boolean) {
        this.loadingRepaymentPlans = loading;
    }

    @Mutation
    private async setLoadingDialog(loading: boolean) {
        this.loadingDialog = loading;
    }

    @Mutation
    private async setLoadingHistory(loading: boolean) {
        this.loadingHistory = loading;
    }

    @Mutation
    private async setLoadingTransactions(loading: boolean) {
        this.loadingTransactions = loading;
    }

    @Mutation
    private async setLoadingPendingTransactions(loading: boolean) {
        this.loadingPendingTransactions = loading;
    }

    @Mutation
    private async setLendingAccounts(accounts: LendingAccount[]) {
        if (accounts) {
            this.lendingAccounts = accounts;
        }
    }

    @Mutation
    private async resetLendingAccounts() {
        this.lendingAccounts = [];
    }

    @Mutation
    private async setLendingAccountsSearch(text: string) {
        this.lendingAccountsSearch = text;
    }

    @Mutation
    private async setLendingAccountsSearchType(type: AccountSearchType) {
        this.lendingAccountsSearchType = type;
    }

    @Mutation
    private async setLendingAccountsTotalPages(total: number) {
        this.totalLendingAccountsPages = Math.ceil(total/this.pageSize);
    }

    @Mutation
    private async setLendingAccount(response: LendingAccount) {
        this.lendingAccount = response;
        this.productFeatureVisibility = ListUtils.productFeatureVisibility(response.productSubType);
    }

    @Mutation
    private async setLendingAccountTransactions(transactions: Transaction[]) {
        if (transactions) {
            this.currentLendingAccountTransactions = transactions;
        }
    }

    @Mutation
    private async setLendingAccountTransactionsTotalPages(total: number) {
        this.totalLendingAccountTransactionsPages = Math.ceil(total/this.pageSize);
    }

    @Mutation
    private async setLendingAccountPendingTransactions(transactions: Transaction[]) {
        if (transactions) {
            this.currentLendingAccountPendingTransactions = transactions;
        }
    }

    @Mutation
    private async setLendingAccountPendingTransactionsTotalPages(total: number) {
        this.totalLendingAccountPendingTransactionsPages = Math.ceil(total/this.pageSize);
    }

    @Mutation
    private async setCurrentLendingAccountTransactionsOrder(order: TransactionsOrderOptions) {
        this.currentLendingAccountTransactionsOrder = order;
    }

    @Mutation
    private async setCurrentLendingAccountTransactionsSearch(text: string) {
        this.currentLendingAccountTransactionsSearch = text;
    }

    @Mutation
    private async setCards(response: Card[]) {
        if (response) {
            this.cards = response;
        }
    }

    @Mutation
    private async setCardsTotalPages(total: number) {
        this.totalCardsPages = Math.ceil(total/this.pageSize);
    }

    @Mutation
    private async setCurrentCard(card: Card) {
        this.currentCard = card;
    }

    @Mutation
    private async setSchedule(schedule: Schedule) {
        if (schedule) {
            this.schedule = schedule;
        }
    }

    @Mutation
    private async setScheduleTotalPages(total: number) {
        this.totalSchedulePages = Math.ceil(total/this.pageSize);
    }

    @Mutation
    private async setRepaymentPlans(repaymentPlans: []) {
        if (repaymentPlans) {
            this.repaymentPlans = repaymentPlans;
        }
    }

    @Mutation
    private async setRepaymentPlansTotalPages(total: number) {
        this.totalRepaymentPlansPages = Math.ceil(total/this.pageSize);
    }

    @Mutation
    private async setCurrentRepaymentPlan(repaymentPlan: {}) {
        this.currentRepaymentPlan = repaymentPlan;
    }

    @Mutation
    private async setProductOptions(response: Product[]) {
        if (response) {
            this.productOptions = response;
        }
    }

    @Mutation
    private async setProductIssueOptions(response: ProductIssue[]) {
        if (response) {
            this.productIssueOptions = response;
        }
    }

    @Mutation
    private async setSimulationMode(simulation: Simulation) {
        this.simulationModeEnabled = true;
    }

    @Mutation
    private async clearSimulationMode() {
        this.simulationModeEnabled = false;
        this.simulation = new Simulation();
    }

    @Mutation
    private async setSimulations(simulations: Simulation[]) {
        this.simulations = simulations;
    }

    @Mutation
    private async setCurrentSimulation(simulation: Simulation) {
        this.simulation = simulation;
    }

    @Mutation
    private async setHistory(response: GetHistoryResponse) {
        this.history = response.results;
    }

}

export const LendingAccountModule = getModule(LendingAccountStore);